import { Badge, Box, Flex, HStack, Image, Text, Tooltip, VStack } from "@chakra-ui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import type { ResponseOf } from "../../../core/api";
import { auth } from "../../../core/auth";
import useApi from "../../../shared/hooks/useApi";
import { phoneFormatter } from "../../../shared/utils/phone-formatter";
import EntityModalButton, {
  EntityModalButtonGroup,
} from "../../entityProfileModal/components/EntityModalButton";
import { entityProfileModalColors } from "../../entityProfileModal/entityProfileModal.theme";
import ProfileWindowSettingsButton from "../../profileWindowSettings/ProfileWindowSettingsButton";
import type { CaregiverProfileBridgeActions } from "../caregiverProfileModal.actions";
import { CaregiverProfileCaregiver } from "../caregiverProfileModal.types";
import {
  formatCaregiverCertifications,
  formatCaregiverProfileValue,
  getCaregiverFullName,
  getCaregiverPhotoUrl,
} from "../caregiverProfileModal.utils";

type Props = {
  actions: CaregiverProfileBridgeActions;
  caregiver: CaregiverProfileCaregiver;
};

type CaregiverUnblockLoginResponse = ResponseOf<
  "get",
  "/agencies/:agencyId/coordinator/:agencyMemberId/caregivers/:caregiverId/show_unblock_login"
>;

function HeaderMetaField(props: { label: string; value: React.ReactNode }) {
  return (
    <Text>
      <Box as="span" fontWeight={700}>
        {props.label}:
      </Box>{" "}
      {props.value}
    </Text>
  );
}

export default function CaregiverProfileHeader(props: Props) {
  const { actions, caregiver } = props;
  const { api } = useApi();
  const queryClient = useQueryClient();
  const isOnboardingAgency = auth.getAuthOrFail().isMedflytAgencyMember;
  const activeFlagsQuery = useQuery({
    queryKey: ["caregiverProfileModal", caregiver.id, "flags"],
    queryFn: () =>
      api.get("./caregiver/:caregiverId/flags", {
        path: { caregiverId: caregiver.id },
      }),
    select: (response) => Object.entries(response.flags).filter(([, active]) => active),
  });
  const attendanceStatsQuery = useQuery({
    queryKey: ["caregiverProfileModal", caregiver.id, "attendanceStats"],
    queryFn: () =>
      api.get("./caregivers/:caregiverId/attendance_stats", {
        path: { caregiverId: caregiver.id },
      }),
    enabled:
      isOnboardingAgency && ["ACTIVE", "ON_HOLD", "ON_LEAVE"].includes(String(caregiver.status)),
  });
  const evvStatsQuery = useQuery({
    queryKey: ["caregiverProfileModal", caregiver.id, "evvStats"],
    queryFn: () =>
      api.get("./caregiver/:caregiverId/evv_compliance", {
        path: { caregiverId: caregiver.id },
      }),
    enabled: !isOnboardingAgency,
  });
  const unblockLoginQuery = useQuery({
    queryKey: ["caregiverProfileModal", caregiver.id, "showUnblockLogin"],
    queryFn: () =>
      api.get(
        "/agencies/:agencyId/coordinator/:agencyMemberId/caregivers/:caregiverId/show_unblock_login",
        { path: { caregiverId: caregiver.id } }
      ),
  });
  const activeFlags = activeFlagsQuery.data ?? [];
  const attendanceStats = attendanceStatsQuery.data;
  const evvStats = evvStatsQuery.data;
  const evvUsage =
    evvStats === undefined || evvStats.evvCount + evvStats.manualCount === 0
      ? 0
      : (evvStats.evvCount / (evvStats.evvCount + evvStats.manualCount)) * 100;
  const photoRingColor = caregiver.appInstalled
    ? caregiver.online
      ? "green.400"
      : "red.400"
    : "gray.300";

  return (
    <Flex alignItems="flex-start" gap={5} justifyContent="space-between" px={8} py={4}>
      <Flex gap={4} minW={0}>
        <Image
          alt=""
          border="3px solid"
          borderColor={photoRingColor}
          borderRadius="full"
          boxSize="70px"
          flexShrink={0}
          objectFit="cover"
          src={getCaregiverPhotoUrl(caregiver)}
        />
        <VStack alignItems="flex-start" gap={1} minW={0}>
          <HStack alignItems="center" flexWrap="wrap" spacing={2}>
            <Text
              color={entityProfileModalColors.hoverTitle}
              fontSize="25px"
              fontWeight={600}
              lineHeight="1.1"
            >
              {getCaregiverFullName(caregiver)}
            </Text>
            {caregiver.displayId !== null ? (
              <Text
                color={entityProfileModalColors.hoverTitle}
                fontSize="22px"
                fontWeight={500}
                lineHeight="1.1"
              >
                ({caregiver.displayId})
              </Text>
            ) : null}
            <Badge
              borderRadius="2px"
              colorScheme={caregiver.status === "ACTIVE" ? "green" : "orange"}
              fontSize="11px"
              px={2}
              py={1}
            >
              {caregiver.status}
            </Badge>
            {caregiver.nextStatus ? (
              <Badge borderRadius="2px" colorScheme="orange" fontSize="11px" px={2} py={1}>
                Will be {caregiver.nextStatus}
                {caregiver.nextStatusEffectiveDate
                  ? ` on ${formatCaregiverProfileValue(caregiver.nextStatusEffectiveDate)}`
                  : ""}
              </Badge>
            ) : null}
            {activeFlags.length > 0 ? (
              <Tooltip label={activeFlags.map(([flag]) => flag).join(", ")}>
                <Badge borderRadius="2px" colorScheme="orange" fontSize="11px" px={2} py={1}>
                  {activeFlags.length} Flag{activeFlags.length === 1 ? "" : "s"}
                </Badge>
              </Tooltip>
            ) : null}
            <Badge
              borderRadius="2px"
              colorScheme={caregiver.appInstalled ? "green" : "pink"}
              fontSize="11px"
              px={2}
              py={1}
            >
              {caregiver.appInstalled ? "App installed" : "Didn't install"}
            </Badge>
            {caregiver.appInstalled ? (
              <Badge
                borderRadius="2px"
                colorScheme={caregiver.online ? "green" : "red"}
                fontSize="11px"
                px={2}
                py={1}
              >
                {caregiver.online ? "Online" : "Offline"}
              </Badge>
            ) : null}
            <Badge
              borderRadius="2px"
              colorScheme={caregiver.isCompliant ? "green" : "red"}
              fontSize="11px"
              px={2}
              py={1}
            >
              {caregiver.isCompliant ? "Compliant" : "Not compliant"}
            </Badge>
          </HStack>
          <Text color={entityProfileModalColors.muted} fontSize="14px" lineHeight="1.25">
            {caregiver.address}
          </Text>
          <HStack
            color={entityProfileModalColors.secondaryText}
            flexWrap="wrap"
            fontSize="14px"
            spacing={3}
          >
            <HeaderMetaField
              label="Phone"
              value={
                caregiver.phoneNumber
                  ? phoneFormatter.formatNationalIfValid(caregiver.phoneNumber)
                  : ""
              }
            />
            <HeaderMetaField
              label="Certifications"
              value={formatCaregiverCertifications(
                caregiver.certifications,
                caregiver.certificationNames
              )}
            />
            <HeaderMetaField
              label="Last seen"
              value={
                caregiver.appInstalled && caregiver.lastSeen
                  ? formatCaregiverProfileValue(caregiver.lastSeen)
                  : ""
              }
            />
            {attendanceStats !== undefined ? (
              <Tooltip
                label={`# Visits: ${
                  attendanceStats.attendedVisits + attendanceStats.callOuts
                }, Attendance: ${attendanceStats.attendedVisits}, Call-Outs: ${
                  attendanceStats.callOuts
                }`}
              >
                <Box as="span">
                  <HeaderMetaField
                    label="Attendance Rate"
                    value={`${Math.round(attendanceStats.attendanceRate)}%`}
                  />
                </Box>
              </Tooltip>
            ) : null}
            {!isOnboardingAgency && evvStats !== undefined ? (
              <Tooltip
                label={`# visits: ${evvStats.totalVisits}, EVV: ${evvStats.evvCount}, Not EVV: ${evvStats.manualCount}`}
              >
                <Box as="span">
                  <HeaderMetaField label="EVV Usage" value={`${Math.round(evvUsage)}%`} />
                </Box>
              </Tooltip>
            ) : null}
          </HStack>
        </VStack>
      </Flex>

      <HStack flexShrink={0} flexWrap="wrap" justifyContent="flex-end" spacing={2}>
        {/* First in the row — furthest from the modal corner — and ahead of the
            conditional Unblock Login button so it does not shift position. */}
        <ProfileWindowSettingsButton />
        {unblockLoginQuery.data?.show === true ? (
          <EntityModalButton
            iconClassName="fa fa-unlock"
            kind="blue"
            size="sm"
            onClick={() =>
              actions.unblockLogin({
                caregiver,
                onDone: () =>
                  queryClient.setQueryData<CaregiverUnblockLoginResponse>(
                    ["caregiverProfileModal", caregiver.id, "showUnblockLogin"],
                    { show: false }
                  ),
              })
            }
          >
            Unblock Login
          </EntityModalButton>
        ) : null}
        <EntityModalButtonGroup>
          <EntityModalButton
            iconClassName="fa fa-paperclip"
            kind="blue"
            size="sm"
            onClick={() => actions.exportProfile({ caregiver })}
          >
            Export Profile
          </EntityModalButton>
          <EntityModalButton
            aria-label="Export profile settings"
            iconClassName="fa fa-cog"
            kind="blue"
            size="sm"
            title="Export profile settings"
            variant="outline"
            onClick={() => actions.openExportProfileSettings({ caregiver })}
          />
        </EntityModalButtonGroup>
        <EntityModalButton
          iconClassName="fa fa-comments-o"
          kind="blue"
          size="sm"
          onClick={() => actions.openChat({ caregiver })}
        >
          {isOnboardingAgency ? "Communications" : "Chat"}
        </EntityModalButton>
        <EntityModalButton
          iconClassName="fa fa-pencil"
          kind="blue"
          size="sm"
          onClick={() => actions.openNotes({ caregiver })}
        >
          Notes
        </EntityModalButton>
      </HStack>
    </Flex>
  );
}
